/**
 * Fait : l'aiguillage de l'atelier — qui est connecté, et quel écran montrer.
 *   Le routage se lit dans le fragment d'URL (`#/projets/…`) : pas de
 *   dépendance de routage pour une dizaine d'écrans, et le bouton
 *   « précédent » du navigateur fonctionne quand même.
 *
 *   LA BARRE S'ASSEMBLE ICI, UNE SEULE FOIS — refonte visuelle du 2026-08-28.
 *   Avant, chaque écran composait la sienne (cinq variantes, dont une notice
 *   sans menu et un chargement sans rien) : « on sent que ce n'est pas
 *   intégré », disait Charles. Désormais sa composition ne dépend que de
 *   l'état de session — sans session, ou connecté (plus l'entrée
 *   d'administration que le serveur réserve au parent) — et l'aiguillage ne
 *   décide plus que du CONTENU.
 *
 * Ne fait pas : il ne décide d'aucun droit et n'appelle aucune règle — il
 *   demande le profil, et le serveur répond. Sans session, on montre la
 *   connexion : c'est le serveur qui l'a dit, pas une supposition du client.
 */

import { useCallback, useEffect, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { GabaritDePage } from '../gabarits/GabaritDePage.tsx';
import { PageNotice } from '../notice/PageNotice.tsx';
import { litLeProfil, seDeconnecte, type ProfilVu } from './api.ts';
import {
  BoutonDeSortie,
  CommandesCommunes,
  LienAdministration,
  LienArgent,
  LienConsignes,
  LienProjets,
  LienMonCompte,
  RetourALAtelier,
} from './BarreDAtelier.tsx';
import { roleEnFrancais } from './roles.ts';
import { routeCourante, type Route } from './routes.ts';
import { PageAdministration } from './PageAdministration.tsx';
import { PageArgent } from './PageArgent.tsx';
import { PageCompte } from './PageCompte.tsx';
import { PageConsignes } from './PageConsignes.tsx';
import { PromesseDeClannik } from './PromesseDeClannik.tsx';
import { PageConnexion } from './PageConnexion.tsx';
import { PageFil } from './PageFil.tsx';
import { PageInscription } from './PageInscription.tsx';
import { PageLienAbime } from './PageLienAbime.tsx';
import { PageNouveauMotDePasse } from './PageNouveauMotDePasse.tsx';
import { PageOubli } from './PageOubli.tsx';
import { PageProjet } from './PageProjet.tsx';
import { PageProjets } from './PageProjets.tsx';

/**
 * Où l'on en est de la question « qui êtes-vous ? » : pas encore demandé,
 * connu, sans session, ou l'atelier ne répond pas.
 *
 * `injoignable` est distinct de `null` À DESSEIN. Les confondre — ce que
 * faisait la première version — envoyait quelqu'un sur l'écran d'entrée à la
 * première coupure de réseau : il croyait avoir été déconnecté, ressaisissait
 * son mot de passe, et échouait encore.
 */
type EtatDuProfil = ProfilVu | null | 'inconnu' | 'injoignable';

/** La barre des tâches, pour qui est entré. L'écran courant se dit. */
function CommandesDeLAtelier({
  profil,
  ecran,
}: {
  readonly profil: ProfilVu;
  readonly ecran: Route['ecran'];
}) {
  return (
    <>
      <LienProjets courant={ecran === 'projets' || ecran === 'projet' || ecran === 'fil'} />
      <LienConsignes courant={ecran === 'consignes'} />
      <LienArgent debutDeGroupe courant={ecran === 'argent'} />
      <LienMonCompte courant={ecran === 'compte'} />
      {profil.role === 'family_admin' && (
        <LienAdministration courant={ecran === 'administration'} />
      )}
      <CommandesCommunes debutDeGroupe ecran={ecran} />
    </>
  );
}

export function Atelier() {
  const [profil, setProfil] = useState<EtatDuProfil>('inconnu');
  const [route, setRoute] = useState<Route>(routeCourante);

  const demandeLeProfil = useCallback(async () => {
    const issue = await litLeProfil();

    if (issue.bon) {
      setProfil(issue.valeur);
      return;
    }

    setProfil(issue.injoignable === true ? 'injoignable' : null);
  }, []);

  const sort = useCallback(async () => {
    const issue = await seDeconnecte();

    if (issue.bon) {
      // On quitte l'écran courant AVANT d'oublier qui l'on est : rester sur
      // `#/compte` ferait revenir la personne sur son compte à la connexion
      // suivante, sans qu'elle l'ait demandé.
      location.hash = '';
      setProfil(null);
      return;
    }

    // Le serveur n'a pas confirmé : on ne PRÉTEND pas être sorti. Afficher
    // l'écran d'entrée alors que la session vit encore serait le mensonge
    // exact que le critère 110 interdit — on redemande, et il tranche.
    await demandeLeProfil();
  }, [demandeLeProfil]);

  useEffect(() => {
    void demandeLeProfil();

    const surFragment = () => setRoute(routeCourante());
    window.addEventListener('hashchange', surFragment);

    return () => window.removeEventListener('hashchange', surFragment);
  }, [demandeLeProfil]);

  const connecte = profil !== null && profil !== 'inconnu' && profil !== 'injoignable';

  /*
   * LA BARRE NE DÉPEND QUE DE L'ÉTAT DE SESSION — jamais de l'écran.
   * Sans session (connexion, courriels, chargement, atelier injoignable),
   * elle porte ce qui a un sens sans compte : l'aide et la vie privée. La
   * notice, rendue avant que le profil n'arrive, commence donc avec cette
   * barre-là et s'étoffe quand le profil répond — un seul saut, au
   * chargement, jamais de menu qui disparaît.
   */
  const commandes = connecte ? (
    <CommandesDeLAtelier profil={profil} ecran={route.ecran} />
  ) : (
    <CommandesCommunes ecran={route.ecran} />
  );

  const sortie = connecte ? <BoutonDeSortie surSortie={() => void sort()} /> : undefined;

  const sousTitre = connecte
    ? `Bonjour ${profil.nomAffiche} — ${roleEnFrancais(profil.role)}.`
    : route.ecran === 'notice'
      ? 'Notice d’utilisation'
      : undefined;

  const contenu = (() => {
    // La notice et la vie privée se lisent AVANT toute question de session :
    // elles n'appellent aucune API, et quelqu'un qui n'arrive pas à entrer
    // doit pouvoir les consulter. Leur retour vers l'écran d'origine est une
    // commande DE PAGE : il vit dans le contenu, pas dans la barre.
    if (route.ecran === 'notice') {
      return (
        <>
          <RetourALAtelier />
          <PageNotice chapitreId={route.chapitre} />
        </>
      );
    }

    if (route.ecran === 'vie-privee') {
      return (
        <>
          <RetourALAtelier />
          <PromesseDeClannik />
        </>
      );
    }

    // Les trois écrans que l'on atteint sans être connecté — retrouver son
    // mot de passe, en choisir un neuf, créer son compte depuis une
    // invitation. Eux non plus n'attendent pas le profil : quelqu'un qui suit
    // le lien de son courriel n'a pas de session.
    if (route.ecran === 'oubli') return <PageOubli />;

    if (route.ecran === 'mot-de-passe') {
      // `key` sur le jeton : sans elle, ouvrir un SECOND lien dans le même
      // onglet gardait l'état du premier — l'écran affichait « c'est fait »
      // sans le moindre champ de saisie.
      return <PageNouveauMotDePasse key={route.jeton} jeton={route.jeton} />;
    }

    if (route.ecran === 'lien-abime') return <PageLienAbime pour={route.pour} />;

    if (route.ecran === 'inscription') {
      return <PageInscription jeton={route.jeton} surEntree={demandeLeProfil} />;
    }

    if (profil === 'inconnu') {
      return (
        <section data-etat="chargement" aria-busy="true">
          <h2>Ouverture de l’atelier…</h2>
        </section>
      );
    }

    // L'atelier ne répond pas — ce n'est PAS une déconnexion, et on ne
    // demande donc pas de se reconnecter. On dit ce qui se passe, et on
    // propose le seul geste utile.
    if (profil === 'injoignable') {
      return (
        <section data-etat="indisponible" data-ecran="injoignable">
          <h2>L’atelier ne répond pas</h2>
          <Annonce urgence="urgent">
            Votre connexion ou l’atelier est momentanément indisponible. Vous n’avez pas été
            déconnecté, et rien n’a été perdu.
          </Annonce>
          <button type="button" data-role="reessayer" onClick={() => void demandeLeProfil()}>
            Réessayer
          </button>
        </section>
      );
    }

    if (profil === null) return <PageConnexion surEntree={() => void demandeLeProfil()} />;

    return (
      <>
        {/*
         * Le bonjour appartient à l'EN-TÊTE, pas au contenu : posé dans le
         * contenu, il restait aligné à gauche pendant que tout le reste
         * suivait la colonne centrale, et se lisait comme un élément égaré.
         */}
        <p className="pour-lecteur-decran" data-role="bonjour">
          Bonjour {profil.nomAffiche} — {roleEnFrancais(profil.role)}.
        </p>

        {route.ecran === 'projets' && <PageProjets profil={profil} />}
        {route.ecran === 'projet' && <PageProjet projetId={route.id} moiId={profil.compteId} />}
        {route.ecran === 'fil' && <PageFil projetId={route.id} filId={route.filId} />}
        {route.ecran === 'administration' && <PageAdministration />}
        {route.ecran === 'consignes' && <PageConsignes />}
        {route.ecran === 'argent' && <PageArgent profil={profil} onglet={route.onglet} />}
        {route.ecran === 'compte' && (
          <PageCompte
            profil={profil}
            /*
             * Le compte n'existe plus : le serveur a déjà effacé le cookie, et
             * redemander le profil ne servirait qu'à faire clignoter un refus.
             * On quitte l'écran AVANT d'oublier qui l'on est, pour la même
             * raison qu'à la déconnexion — rester sur `#/compte` ramènerait la
             * personne suivante sur un compte disparu.
             */
            surDepart={() => {
              location.hash = '';
              setProfil(null);
            }}
          />
        )}
      </>
    );
  })();

  return (
    <GabaritDePage titre="Clannik" sousTitre={sousTitre} commandes={commandes} sortie={sortie}>
      {contenu}
    </GabaritDePage>
  );
}
